import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { ApiError } from '../../services/api';
import { deliveryConfigApi } from '../../services/deliveryConfigApi';
import { DeliveryMargin } from '../../types/delivery';
import DealerCreditCard from '../../components/finance/DealerCreditCard';
import MarginCard from '../../components/finance/MarginCard';
import PromotionsCapCard from '../../components/finance/PromotionsCapCard';
import ServiceFeeCard from '../../components/finance/ServiceFeeCard';
import TabbyCard from '../../components/finance/TabbyCard';
import ZofeurCard from '../../components/finance/ZofeurCard';

/**
 * #1113 — Finance → Configuration, gated by the `finance-config` screen: the money settings an admin
 * touches, top to bottom: the service fee, Tabby and Zofeur (#1251) (editable cards over their Runtime
 * Config keys, saved atomically), the dealer credit settings (Spec 085), the month-to-date delivery
 * margin (Spec 053 F6, AC-41) and the promotions cap (Spec 059 Flow E).
 *
 * Every card loads on its own, so one failing service never takes the others down. The delivery fee
 * and the recovery providers moved to Finance → Delivery & Recovery (`DeliveryTab`).
 */
export default function ConfigurationTab() {
  const auth = useAuth();
  const token = auth.token as string;

  const [margin, setMargin] = useState<DeliveryMargin | null>(null);
  const [marginLoading, setMarginLoading] = useState(true);
  const [marginError, setMarginError] = useState<string | null>(null);
  const [marginUnavailable, setMarginUnavailable] = useState(false);

  const loadMargin = useCallback(async () => {
    if (!auth.token) return;
    setMarginLoading(true);
    setMarginError(null);
    setMarginUnavailable(false);
    try {
      setMargin(await deliveryConfigApi.getMargin(auth.token));
    } catch (err) {
      setMargin(null);
      // 404 = the orders service does not serve this endpoint on this environment yet.
      setMarginUnavailable(err instanceof ApiError && err.status === 404);
      setMarginError(err instanceof Error ? err.message : 'Failed to load the delivery margin');
    } finally {
      setMarginLoading(false);
    }
  }, [auth.token]);

  useEffect(() => { loadMargin(); }, [loadMargin]);

  return (
    <div data-testid="configuration-tab">
      <ServiceFeeCard token={token} />
      <TabbyCard token={token} />
      <ZofeurCard token={token} />
      <DealerCreditCard token={token} />
      <MarginCard
        margin={margin}
        loading={marginLoading}
        error={marginError}
        unavailable={marginUnavailable}
        onRetry={loadMargin}
      />
      <PromotionsCapCard token={token} />
    </div>
  );
}
